/**
 * /admin/staff — Admin-only staff management console.
 *
 * Capabilities:
 *  • Table of every staff account with role, active status, last-login, 2FA
 *  • Create new staff (Manager / Accountant / Receptionist / Operator)
 *    → returns an initial password shown ONCE, with copy/download helpers
 *  • Inline enable/disable toggle
 *  • Reset-password (returns new temp password shown once)
 *  • Edit name + role
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserPlus,
  Search,
  ShieldCheck,
  ShieldOff,
  KeyRound,
  Loader2,
  ArrowLeft,
  Copy,
  Download,
  CheckCircle2,
  AlertCircle,
  Ban,
  Play,
  Pencil,
  ChevronDown,
} from "lucide-react";
import { AdminStaff, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";

const ROLE_META = {
  admin:        { label: "Admin",        color: "text-[#F0D788]", bg: "bg-[#D4AF37]/10 border-[#D4AF37]/40" },
  manager:      { label: "Manager",      color: "text-blue-200",  bg: "bg-blue-500/10 border-blue-400/30" },
  accountant:   { label: "Accountant",   color: "text-emerald-200", bg: "bg-emerald-500/10 border-emerald-400/30" },
  receptionist: { label: "Receptionist", color: "text-pink-200",  bg: "bg-pink-500/10 border-pink-400/30" },
  operator:     { label: "Operator",     color: "text-white/85",  bg: "bg-white/10 border-white/20" },
};

export default function AdminStaffPage() {
  const nav = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  // Create modal
  const [showCreate, setShowCreate] = useState(false);

  // Password reveal modal (shared between create + reset)
  const [reveal, setReveal] = useState(null); // {email, password, mode:'create'|'reset'}

  // Edit modal
  const [editRow, setEditRow] = useState(null);

  // Delete/disable confirm
  const [confirmRow, setConfirmRow] = useState(null);

  useEffect(() => {
    if (!tokenStorage.get()) {
      nav("/admin", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const d = await AdminStaff.list();
      setStaff(d.items || []);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin", { replace: true });
      if (e?.response?.status === 403) {
        setError("This page is admin-only.");
      } else {
        setError("Could not load staff list.");
      }
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return staff.filter((s) => {
      if (roleFilter && s.role !== roleFilter) return false;
      if (!needle) return true;
      return (
        (s.email || "").toLowerCase().includes(needle) ||
        (s.name || "").toLowerCase().includes(needle)
      );
    });
  }, [staff, q, roleFilter]);

  async function toggleActive(row) {
    setError("");
    try {
      if (row.active) await AdminStaff.disable(row.id);
      else await AdminStaff.enable(row.id);
      setNotice(`${row.email} is now ${row.active ? "disabled" : "enabled"}.`);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to toggle status.");
    }
  }

  async function doResetPassword(row) {
    setError("");
    try {
      const r = await AdminStaff.resetPassword(row.id);
      setReveal({ email: row.email, password: r.temp_password, mode: "reset" });
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Reset failed.");
    }
  }

  return (
    <div data-testid="admin-staff-page" className="min-h-screen bg-[#04102B] text-white">
      <header className="border-b border-white/10 backdrop-blur-lg bg-black/20 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="staff-back"
            onClick={() => nav("/admin/leads")}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F0D788]"
          >
            <ArrowLeft size={14} /> Back to Admin
          </button>
          <div className="flex items-center gap-3">
            <Logo size={40} testId="staff-logo" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Staff Management</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-14 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Team & Access</h1>
            <p className="mt-2 text-white/75 text-sm max-w-2xl">
              Create accounts for Managers, Accountants, Receptionists and Operators. Each staff
              member signs in from the same admin login with their own credentials — permissions
              are enforced by role, both in the UI and in the backend.
            </p>
          </div>
          <button
            data-testid="staff-create-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold"
          >
            <UserPlus size={15} /> New staff account
          </button>
        </div>

        {notice && (
          <div data-testid="staff-notice" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100 flex items-center gap-2">
            <CheckCircle2 size={14} /> {notice}
          </div>
        )}
        {error && (
          <div data-testid="staff-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[240px] relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="staff-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or name…"
              className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 focus:border-[#D4AF37]/60 outline-none text-sm"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {["", ...Object.keys(ROLE_META)].map((r) => (
              <button
                key={r || "all"}
                data-testid={`staff-filter-${r || "all"}`}
                onClick={() => setRoleFilter(r)}
                className={`px-3.5 py-1.5 rounded-full text-xs border transition ${
                  roleFilter === r
                    ? "bg-[#D4AF37]/20 border-[#D4AF37]/60 text-[#F0D788]"
                    : "bg-white/[0.03] border-white/10 text-white/65 hover:text-white"
                }`}
              >
                {r ? ROLE_META[r].label : "All roles"}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div data-testid="staff-table" className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.4fr_0.8fr_0.8fr_1fr_0.9fr] gap-3 px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-white/65 bg-black/25 border-b border-white/10">
            <div>Staff</div>
            <div>Role</div>
            <div>Status</div>
            <div>Last login</div>
            <div className="text-right">Actions</div>
          </div>

          {loading && (
            <div className="py-14 text-center text-white/50 text-sm flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading team…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-14 text-center text-white/50 text-sm">
              No staff match your filters.
            </div>
          )}
          {!loading && filtered.map((row) => {
            const rm = ROLE_META[row.role] || ROLE_META.operator;
            const isAdmin = row.role === "admin";
            return (
              <div
                key={row.id}
                data-testid={`staff-row-${row.email}`}
                className="grid grid-cols-1 md:grid-cols-[1.4fr_0.8fr_0.8fr_1fr_0.9fr] gap-2 md:gap-3 md:items-center px-4 md:px-5 py-3.5 border-b border-white/[0.06] hover:bg-white/[0.02] text-sm"
              >
                <div>
                  <div className="text-white font-medium">{row.name || "—"}</div>
                  <div className="text-xs text-white/50 mt-0.5">{row.email}</div>
                </div>
                <div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${rm.bg} ${rm.color}`}>
                    {rm.label}
                  </span>
                  {row.twofa_enabled && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-300">
                      <ShieldCheck size={10} /> 2FA
                    </span>
                  )}
                </div>
                <div>
                  {row.active ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-emerald-500/10 text-emerald-200 border border-emerald-400/25">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-red-500/10 text-red-200 border border-red-400/25">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400" /> Disabled
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/65">
                  {row.last_login_at ? formatDate(row.last_login_at) : <span className="text-white/40">Never</span>}
                </div>
                <div className="flex items-center justify-end gap-1.5">
                  {!isAdmin && (
                    <>
                      <button
                        title="Edit"
                        data-testid={`staff-edit-${row.email}`}
                        onClick={() => setEditRow(row)}
                        className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/[0.06]"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        title="Reset password"
                        data-testid={`staff-reset-${row.email}`}
                        onClick={() => setConfirmRow({ row, action: "reset" })}
                        className="p-2 rounded-full text-white/70 hover:text-[#F0D788] hover:bg-[#D4AF37]/10"
                      >
                        <KeyRound size={13} />
                      </button>
                      <button
                        title={row.active ? "Disable" : "Enable"}
                        data-testid={`staff-toggle-${row.email}`}
                        onClick={() => setConfirmRow({ row, action: row.active ? "disable" : "enable" })}
                        className={`p-2 rounded-full ${
                          row.active
                            ? "text-red-200 hover:bg-red-500/10"
                            : "text-emerald-200 hover:bg-emerald-500/10"
                        }`}
                      >
                        {row.active ? <Ban size={13} /> : <Play size={13} />}
                      </button>
                    </>
                  )}
                  {isAdmin && <span className="text-[10px] text-white/35 italic">Seed admin</span>}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {showCreate && (
        <CreateStaffModal
          onClose={() => setShowCreate(false)}
          onCreated={(r) => {
            setShowCreate(false);
            setReveal({ email: r.email, password: r.initial_password, mode: "create" });
            refresh();
          }}
        />
      )}

      {editRow && (
        <EditStaffModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); refresh(); setNotice("Staff account updated."); }}
        />
      )}

      {confirmRow && (
        <ConfirmActionModal
          row={confirmRow.row}
          action={confirmRow.action}
          onClose={() => setConfirmRow(null)}
          onConfirm={async () => {
            const row = confirmRow.row;
            const action = confirmRow.action;
            setConfirmRow(null);
            if (action === "reset") await doResetPassword(row);
            else await toggleActive(row);
          }}
        />
      )}

      {reveal && (
        <PasswordRevealModal
          info={reveal}
          onClose={() => setReveal(null)}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Sub-components
// ------------------------------------------------------------
function CreateStaffModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("operator");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await AdminStaff.create({ email: email.trim(), name: name.trim(), role });
      onCreated(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create account.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Create staff account" onClose={onClose} testId="staff-create-modal">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name">
          <input
            data-testid="staff-create-name"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="staff-create-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="name@sahyogtapi.in"
          />
        </Field>
        <Field label="Role">
          <div className="relative">
            <select
              data-testid="staff-create-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={`${inputCls} appearance-none pr-9`}
            >
              <option value="manager">Manager</option>
              <option value="accountant">Accountant</option>
              <option value="receptionist">Receptionist</option>
              <option value="operator">Operator</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
          </div>
        </Field>
        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200 flex items-center gap-2">
            <AlertCircle size={12} /> {err}
          </div>
        )}
        <div className="pt-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="staff-create-submit"
            disabled={busy || !email || !name}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
          >
            {busy ? <><Loader2 size={13} className="animate-spin" /> Creating…</> : <><UserPlus size={13} /> Create</>}
          </button>
        </div>
      </form>
      <p className="mt-4 text-[11px] text-white/45 leading-relaxed">
        A strong random password will be generated. Copy it before closing the reveal panel —
        we never store or display it again. The staff member will be asked to change it on
        first login.
      </p>
    </Shell>
  );
}

function EditStaffModal({ row, onClose, onSaved }) {
  const [name, setName] = useState(row.name || "");
  const [role, setRole] = useState(row.role);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await AdminStaff.update(row.id, { name, role });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Update failed.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Shell title={`Edit — ${row.email}`} onClose={onClose} testId="staff-edit-modal">
      <form onSubmit={save} className="space-y-4">
        <Field label="Full name">
          <input data-testid="staff-edit-name" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Role">
          <div className="relative">
            <select data-testid="staff-edit-role" value={role} onChange={(e) => setRole(e.target.value)} className={`${inputCls} appearance-none pr-9`}>
              <option value="manager">Manager</option>
              <option value="accountant">Accountant</option>
              <option value="receptionist">Receptionist</option>
              <option value="operator">Operator</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
          </div>
        </Field>
        {err && <div className="text-xs text-red-200">{err}</div>}
        <div className="pt-1 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75">Cancel</button>
          <button type="submit" data-testid="staff-edit-submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 rounded-full btn-gold text-sm font-semibold disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : "Save changes"}
          </button>
        </div>
      </form>
    </Shell>
  );
}

function ConfirmActionModal({ row, action, onClose, onConfirm }) {
  const copy = {
    reset:   { title: "Reset password?",  body: `Generate a new random password for ${row.email}. The previous password will stop working immediately.`, btn: "Reset password", color: "btn-gold" },
    disable: { title: "Disable account?", body: `${row.email} will not be able to sign in until you enable them again. Any active session continues until token expiry.`, btn: "Disable", color: "bg-red-500 hover:bg-red-600 text-white" },
    enable:  { title: "Enable account?",  body: `${row.email} will be able to sign in again.`, btn: "Enable", color: "bg-emerald-500 hover:bg-emerald-600 text-white" },
  }[action];
  return (
    <Shell title={copy.title} onClose={onClose} testId={`staff-confirm-${action}-modal`}>
      <p className="text-sm text-white/75">{copy.body}</p>
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75">Cancel</button>
        <button
          type="button"
          data-testid={`staff-confirm-${action}-yes`}
          onClick={onConfirm}
          className={`px-5 py-2 rounded-full text-sm font-semibold ${copy.color}`}
        >
          {copy.btn}
        </button>
      </div>
    </Shell>
  );
}

function PasswordRevealModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(`Email: ${info.email}\nPassword: ${info.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }
  function download() {
    const blob = new Blob(
      [
        `Sahyog Admin — Staff Account\n`,
        `Generated: ${new Date().toISOString()}\n\n`,
        `Email:    ${info.email}\n`,
        `Password: ${info.password}\n\n`,
        `This password is shown ONCE. The staff member will be asked to change it\n`,
        `on their first sign-in.\n`,
      ],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sahyog-staff-${info.email.split("@")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <Shell title={info.mode === "reset" ? "New temporary password" : "Account created"} onClose={onClose} testId="staff-reveal-modal">
      <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-200">Password (shown once)</div>
        <div data-testid="staff-reveal-password" className="mt-2 font-mono text-2xl md:text-3xl text-[#FBE9A2] tracking-wide break-all">
          {info.password}
        </div>
        <div className="mt-3 text-xs text-white/60">
          Account: <span className="text-white/90">{info.email}</span>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <button
          data-testid="staff-reveal-copy"
          onClick={copy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/40 text-sm text-[#F0D788] hover:bg-[#D4AF37]/10"
        >
          <Copy size={13} /> {copied ? "Copied!" : "Copy credentials"}
        </button>
        <button
          onClick={download}
          data-testid="staff-reveal-download"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-sm text-white/85 hover:bg-white/[0.06]"
        >
          <Download size={13} /> Download .txt
        </button>
        <button
          data-testid="staff-reveal-close"
          onClick={onClose}
          className="ml-auto px-4 py-2 rounded-full text-sm text-white/60 hover:text-white"
        >
          I've saved it
        </button>
      </div>
    </Shell>
  );
}

// ---------- Primitives ----------
const inputCls = "w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/15 focus:border-[#D4AF37]/60 outline-none text-sm text-white";

function Shell({ title, testId, onClose, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid={testId} className="w-full max-w-md rounded-3xl bg-[#0A1F44] border border-[#D4AF37]/30 shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6">
          <div className="font-display text-lg text-white">{title}</div>
          <button onClick={onClose} className="text-white/50 hover:text-white">✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
